'use client'

import { FadeInSlideUp } from '@/components/shared/animations'
import { richTextRenderer } from '@/components/shared/rich-text'
import { Heading } from '@/components/shared/typography'
import { TextBlockFieldsFragment } from '@/graphql/types'
import { Event } from '@/services/sched/types'
import { getNow } from '@/utils/time'
import { Container, Text } from '@ccpgames/eve-web-core'
import { useInViewport } from '@ccpgames/eve-web-hooks'
import { IconArrowRight, IconLayoutGridFilled, IconLayoutListFilled } from '@tabler/icons-react'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Confetti } from './confetti'
import { findPartyEvent } from './scheduleHelpers'
import styles from './Schedule.module.css'

type ScheduleDescription = TextBlockFieldsFragment['text']
const TIME_SLOT_HEIGHT = 120 // pixels
const TIME_SLOT_GAP = 16 // pixels

type ViewType = 'grid' | 'compact'

interface TimeSlot {
  time: string
  events: { [venue: string]: Event | null }
  occupiedBy?: { [venue: string]: Event } // Track which longer events occupy this slot
}

interface PopupState {
  isOpen: boolean
  event: Event | null
  position: { x: number; y: number }
  showAbove: boolean
  centered: boolean
}

const getUniqueVenues = (events: Event[]): string[] => {
  const venues = new Set<string>()
  events.forEach((event) => {
    if (event.venue) {
      venues.add(event.venue)
    }
  })
  return Array.from(venues).sort()
}

const getUniqueDays = (events: Event[]): string[] => {
  // Use a Map to store the first event for each date
  const dateMap = new Map<string, string>()

  events.forEach((event) => {
    const date = event.start_date
    // Only store the first event's date for each day
    if (!dateMap.has(date)) {
      dateMap.set(date, date)
    }
  })

  // Sort dates chronologically
  return Array.from(dateMap.keys()).sort()
}

const groupEventsByVenue = (events: Event[]): { [venue: string]: Event[] } => {
  return events.reduce<{ [venue: string]: Event[] }>((acc, event) => {
    const venue = event.venue || 'Other'
    if (!acc[venue]) {
      acc[venue] = []
    }
    acc[venue].push(event)
    return acc
  }, {})
}

const groupEventsByDay = (events: Event[]): { [key: string]: Event[] } => {
  return events.reduce<{ [key: string]: Event[] }>((acc, event) => {
    const day = event.start_date
    if (!acc[day]) {
      acc[day] = []
    }
    acc[day].push(event)
    return acc
  }, {})
}

const formatTime = (dateString: string) => {
  return format(new Date(dateString), 'HH:mm')
}

const getEventState = (event: Event) => {
  const eventStart = new Date(event.event_start)
  const eventEnd = new Date(event.event_end)
  const now = getNow()
  if (now > eventEnd) {
    return 'pastEvent'
  }
  if (now >= eventStart && now <= eventEnd) {
    return 'liveEvent'
  }
  return ''
}

const EventCard = ({ event }: { event: Event }) => {
  const [popupState, setPopupState] = useState<PopupState>({
    isOpen: false,
    event: null,
    position: { x: 0, y: 0 },
    showAbove: false,
    centered: false,
  })
  const cardRef = useRef<HTMLDivElement>(null)
  const touchTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    if (!cardRef.current) return

    // Get the roomColumn element (parent container)
    const roomColumn = cardRef.current.closest(`.${styles.roomColumn}`)
    let shouldShowAbove = false
    let shouldCenter = false
    if (roomColumn) {
      // Get the distances from the card to the top and bottom of the roomColumn
      const roomColumnRect = roomColumn.getBoundingClientRect()
      const cardRect = cardRef.current.getBoundingClientRect()
      const distanceToBottom = roomColumnRect.bottom - cardRect.bottom
      const distanceToTop = cardRect.top - roomColumnRect.top

      // Set position based on distances
      shouldShowAbove = distanceToBottom < 400
      shouldCenter = distanceToTop < 100
    }
    setPopupState({
      isOpen: true,
      event,
      position: { x: 0, y: 0 },
      showAbove: shouldShowAbove,
      centered: shouldCenter,
    })
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if we're moving to the popup
    const toElement = e.relatedTarget as HTMLElement
    if (toElement?.closest && toElement?.closest(`.${styles.popup}`)) {
      return
    }
    setPopupState({
      isOpen: false,
      event: null,
      position: { x: 0, y: 0 },
      showAbove: false,
      centered: false,
    })
  }

  const handleTouchStart = () => {
    if (!cardRef.current) return

    // Clear any existing timeout
    if (touchTimeout?.current) {
      clearTimeout(touchTimeout?.current)
    }

    // Get the roomColumn element (parent container)
    const roomColumn = cardRef.current.closest(`.${styles.roomColumn}`)
    let shouldShowAbove = false
    let shouldCenter = false
    if (roomColumn) {
      // Get the distances from the card to the top and bottom of the roomColumn
      const roomColumnRect = roomColumn.getBoundingClientRect()
      const cardRect = cardRef.current.getBoundingClientRect()
      const distanceToBottom = roomColumnRect.bottom - cardRect.bottom
      const distanceToTop = cardRect.top - roomColumnRect.top

      // Set position based on distances
      shouldShowAbove = distanceToBottom < 400
      shouldCenter = distanceToTop < 100
    }

    setPopupState({
      isOpen: true,
      event,
      position: { x: 0, y: 0 },
      showAbove: shouldShowAbove,
      centered: shouldCenter,
    })
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Prevent the click event from firing
    e.preventDefault()

    // Add a small delay before closing to allow for popup interaction
    touchTimeout.current = setTimeout(() => {
      const target = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY)
      if (target?.closest(`.${styles.popup}`)) {
        return
      }
      setPopupState({
        isOpen: false,
        event: null,
        position: { x: 0, y: 0 },
        showAbove: false,
        centered: false,
      })
    }, 300)
  }

  useEffect(() => {
    return () => {
      if (touchTimeout?.current) {
        clearTimeout(touchTimeout?.current)
      }
    }
  }, [])

  if (event.name === 'Party at the Top of the World') {
    return <></>
  }

  return (
    <div className={styles.eventCardWrapper}>
      <div
        ref={cardRef}
        className={`${styles.eventCard} ${styles[getEventState(event)]}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.eventCardContent}>
          <div className={styles.eventCardTitleWrapper}>
            <div className={styles.eventMeta}>
              <span className={styles.eventTime}>
                {formatTime(event.event_start)} - {formatTime(event.event_end)}
              </span>
            </div>
            <div className={styles.eventTitle}>
              {event.name}{' '}
              {event.venue && (
                <span className={styles.venueLabel} aria-hidden="true">
                  {event.venue}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {popupState.isOpen && (
        <div
          className={`${styles.popup} ${styles[getEventState(event)]} ${popupState.centered && popupState.showAbove ? styles.popupCentered : popupState.showAbove ? styles.popupAbove : ''}`}
          onMouseLeave={handleMouseLeave}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.popupSpeakersTitle}>Venue</div>
          <div>
            <div className={styles.popupSpeakers}>
              <Text size="sm" className={styles.speaker}>
                {event.venue}
              </Text>
            </div>
          </div>
          {event.speakers && event.speakers.length > 0 && (
            <>
              <div className={styles.popupSpeakersTitle}>Speakers</div>
              <div className={styles.popupSpeakers}>
                {event.speakers.map((speaker) => (
                  <Text key={speaker.id} size="sm" className={styles.speaker}>
                    {speaker.name}
                  </Text>
                ))}
              </div>
            </>
          )}
          {event.description && (
            <Text size="sm" className={styles.popupDescription}>
              {event.description}
            </Text>
          )}
        </div>
      )}
    </div>
  )
}

interface ScheduleProps {
  events: Event[]
  title: string
  description: ScheduleDescription
}

const CompactEvent = ({ event }: { event: Event }) => {
  const [showPopup, setShowPopup] = useState(false)

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if the mouse is still within the eventCardWrapper
    const relatedTarget = e.relatedTarget as Node
    const eventCardWrapper = e.currentTarget.closest(`.${styles.eventCardWrapper}`)

    if (!eventCardWrapper || !eventCardWrapper.contains(relatedTarget)) {
      setShowPopup(false)
    }
  }

  return (
    <div className={styles.eventCardWrapper}>
      <div
        className={`${styles.compactEvent} ${styles[getEventState(event)]}`}
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.eventTime}>
          {formatTime(event.event_start)} - {formatTime(event.event_end)}
        </div>
        <div className={styles.eventTitle}>{event.name}</div>
      </div>
      {showPopup && (
        <div className={`${styles.popup} ${styles[getEventState(event)]}`} onMouseLeave={handleMouseLeave}>
          {event.speakers && event.speakers.length > 0 && (
            <>
              <div className={styles.speakersTitle}>Speakers</div>
              <div className={styles.popupSpeakers}>
                {event.speakers.map((speaker) => (
                  <Text key={speaker.id} size="sm" className={styles.speaker}>
                    {speaker.name}
                  </Text>
                ))}
              </div>
            </>
          )}
          {event.description && (
            <Text size="sm" className={styles.popupDescription}>
              {event.description}
            </Text>
          )}
        </div>
      )}
    </div>
  )
}
const CompactEventList = ({ title, events }: { title: string; events: Event[] }) => {
  return (
    <div className={styles.compactVenue}>
      <Text component="h2" className={styles.venueTitle}>
        {title}
      </Text>
      <div className={styles.compactEventList}>
        {events.map((event) => (
          <CompactEvent key={event.id} event={event} />
        ))}
      </div>
    </div>
  )
}

const Schedule = ({ events, title, description }: ScheduleProps) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [activeTab, setActiveTab] = useState<string>('')
  const [view, setView] = useState<ViewType>((searchParams.get('view') as ViewType) || 'compact')
  const { ref, inViewport } = useInViewport()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (inViewport) {
      setVisible(true)
    }
  }, [inViewport])

  // Update URL when view or date changes
  const updateURL = useCallback(
    (newView?: ViewType, newDate?: string) => {
      const params = new URLSearchParams(searchParams.toString())
      if (newView) params.set('view', newView)
      if (newDate) params.set('date', newDate)
      router.push(`${pathname}?${params.toString()}`, { scroll: false })
    },
    [pathname, router, searchParams],
  )

  // Update view in URL
  const updateViewInURL = useCallback(
    (newView: ViewType) => {
      updateURL(newView, activeTab)
      setView(newView)
    },
    [activeTab, updateURL],
  )

  // Update active tab and URL
  const updateActiveTab = useCallback(
    (newTab: string) => {
      setActiveTab(newTab)
      updateURL(view, newTab)
    },
    [updateURL, view],
  )

  // Get unique days and venues
  const { sortedDays, venues } = useMemo(() => {
    const days = getUniqueDays(events)
    const venueList = getUniqueVenues(events)
    return {
      sortedDays: days,
      venues: venueList,
    }
  }, [events])

  // Group events by day
  const eventsByDay = useMemo(() => groupEventsByDay(events), [events])

  // Create time slots for a given day's events
  const createTimeSlots = (dayEvents: Event[]): TimeSlot[] => {
    // Get all unique times (both start and end times)
    const times = new Set<string>()

    // Find earliest and latest event times
    let earliestTime: Date | null = null
    let latestTime: Date | null = null

    dayEvents.forEach((event) => {
      if (event.name === 'Party at the Top of the World') {
        return
      }
      const startTime = new Date(event.event_start)
      let endTime = new Date(event.event_end)
      if (+endTime - +startTime > 1000 * 60 * 60 * 5) {
        endTime = new Date(startTime.getTime() + 1000 * 60 * 60 * 0.5)
      }

      if (!earliestTime || startTime < earliestTime) {
        earliestTime = startTime
      }
      if (!latestTime || endTime > latestTime) {
        latestTime = endTime
      }
    })

    if (!earliestTime || !latestTime) {
      return []
    }

    // Create copies to avoid mutating the original dates
    const startTime = new Date(earliestTime)
    const endTime = new Date(latestTime)

    // Round earliest time down to previous 15min interval
    startTime.setMinutes(Math.floor(startTime.getMinutes() / 15) * 15)
    startTime.setSeconds(0)
    startTime.setMilliseconds(0)

    // Round latest time up to next 15min interval
    endTime.setMinutes(Math.ceil(endTime.getMinutes() / 15) * 15)
    endTime.setSeconds(0)
    endTime.setMilliseconds(0)

    // Add 15-minute intervals between earliest and latest
    let currentTime = new Date(startTime)
    while (currentTime <= endTime) {
      times.add(currentTime.toISOString())
      currentTime = new Date(currentTime.getTime() + 15 * 60 * 1000) // Add 15 minutes
    }

    // Add event times
    dayEvents.forEach((event) => {
      if (event.name === 'Party at the Top of the World') {
        return
      }
      // Add start and end times
      const startTime = new Date(event.event_start)
      // Round to previous 15min interval
      startTime.setMinutes(Math.floor(startTime.getMinutes() / 15) * 15)
      startTime.setSeconds(0)
      startTime.setMilliseconds(0)
      let endTime = new Date(event.event_end)
      if (+endTime - +startTime > 1000 * 60 * 60 * 5) {
        endTime = new Date(startTime.getTime() + 1000 * 60 * 60 * 0.5)
      }
      // Round to next 15min interval
      endTime.setMinutes(Math.ceil(endTime.getMinutes() / 15) * 15)
      endTime.setSeconds(0)
      endTime.setMilliseconds(0)

      times.add(startTime.toISOString())
      times.add(endTime.toISOString())

      // Add 15-minute intervals between start and end
      const currentTime = new Date(startTime.getTime())
      while (currentTime < endTime) {
        // Add 15 minutes
        currentTime.setMinutes(currentTime.getMinutes() + 15)

        // Only add if it's before the end time
        if (currentTime < endTime) {
          times.add(currentTime.toISOString())
        }
      }
    })

    // Sort times chronologically
    const sortedTimes = Array.from(times).sort()

    // Create time slots with empty spaces for each venue
    const slots = sortedTimes.map((time) => ({
      time,
      events: venues.reduce<{ [venue: string]: Event | null }>((acc, venue) => {
        acc[venue] = null
        return acc
      }, {}),
      occupiedBy: {},
    }))

    // Fill in events and track which slots they occupy
    dayEvents.forEach((event) => {
      const startTime = new Date(event.event_start)
      // Round to previous 15min interval
      startTime.setMinutes(Math.floor(startTime.getMinutes() / 15) * 15)
      startTime.setSeconds(0)
      startTime.setMilliseconds(0)
      let endTime = new Date(event.event_end)
      if (+endTime - +startTime > 1000 * 60 * 60 * 5) {
        endTime = new Date(startTime.getTime() + 1000 * 60 * 60 * 0.5)
      }
      // Round to next 15min interval
      endTime.setMinutes(Math.ceil(endTime.getMinutes() / 15) * 15)
      endTime.setSeconds(0)
      endTime.setMilliseconds(0)

      const startIndex = sortedTimes.indexOf(startTime.toISOString())
      const endIndex = sortedTimes.indexOf(endTime.toISOString())

      if (startIndex >= 0 && endIndex > startIndex && event.venue) {
        // Place the event in its start slot
        slots[startIndex].events[event.venue] = event

        // Mark subsequent slots as occupied by this event, but not if another event starts there
        for (let i = startIndex + 1; i < endIndex; i++) {
          const slotTime = slots[i].time
          const hasEventStarting = dayEvents.some(
            (e) => e !== event && e.venue === event.venue && new Date(e.event_start).toISOString() === slotTime,
          )
          if (!hasEventStarting) {
            slots[i].occupiedBy[event.venue] = event
          }
        }
      }
    })

    return slots
  }

  const EventGrid = ({
    timeSlots,
    venues,
    dayEvents,
  }: {
    timeSlots: TimeSlot[]
    venues: string[]
    dayEvents: Event[]
  }) => {
    // Filter timeline slots to only show full hours
    const timelineSlots = timeSlots.filter((slot) => {
      const date = new Date(slot.time)
      return date.getMinutes() === 0
    })

    return (
      <div className={styles.roomGridWrapper}>
        <div className={styles.timeline}>
          {timelineSlots.map((slot, index) => (
            <div key={`timeline-${index}`} className={styles.timelineSlot}>
              {formatTime(slot.time)}
            </div>
          ))}
        </div>
        <div className={styles.gridLines}>
          {timeSlots.map((slot, index) => {
            const date = new Date(slot.time)
            const isHourMark = date.getMinutes() === 0
            const isHalfHourMark = date.getMinutes() === 30
            if (!isHourMark && !isHalfHourMark) {
              return null
            }
            return (
              <div
                key={`gridline-${index}`}
                className={styles.gridLine}
                style={{
                  opacity: isHourMark ? 1 : 0.2,
                }}
              />
            )
          })}
        </div>
        <div className={styles.roomGrid}>
          {venues.map((venue) => {
            if (!groupEventsByVenue(dayEvents)[venue]?.length) return null

            return (
              <div key={venue} className={styles.roomColumn}>
                <Text component="h2" className={styles.venueTitle}>
                  {venue}
                </Text>
                <div className={styles.eventsList}>
                  {timeSlots.map((slot, index) => {
                    // If this slot is occupied by a previous event AND no event starts here
                    if (slot.occupiedBy?.[venue] && !slot.events[venue]) {
                      return
                    }

                    // If this slot has an event starting here
                    if (slot.events[venue]) {
                      const event = slot.events[venue]!
                      let duration = (+new Date(event.event_end) - +new Date(event.event_start)) / (1000 * 60 * 15)
                      let cappedEvent = false
                      if (duration > 20) {
                        duration = 2
                        cappedEvent = true
                      }

                      // Calculate total height based on duration and gap
                      const totalHeight = (TIME_SLOT_HEIGHT / 4) * duration + (duration - 1) * (TIME_SLOT_GAP / 4)

                      return (
                        <div
                          key={`${venue}-${slot.time}-${index}`}
                          className={cx(
                            styles.timeSlot,
                            styles.cardContainer,
                            duration > 4 ? styles.largeCard : null,
                            cappedEvent ? styles.cappedEvent : null,
                          )}
                          style={{
                            height: duration > 0 ? `${totalHeight}px` : undefined,
                            marginBottom: duration > 0 ? `${TIME_SLOT_GAP / 4}px` : undefined,
                          }}
                        >
                          <EventCard event={event} />
                        </div>
                      )
                    }

                    // Empty slot
                    return (
                      <div key={`${venue}-${slot.time}-${index}`} className={styles.timeSlot}>
                        <div className={styles.emptySlot} />
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderDesktopView = (dayEvents: Event[]) => {
    // Split events into morning and evening sessions
    const morningEvents = dayEvents.filter((event) => {
      const startTime = new Date(event.event_start)
      const hours = startTime.getHours()
      const minutes = startTime.getMinutes()
      if (event.end_date === '2025-04-29') {
        return hours < 16
      }
      return hours < 17 || (hours === 17 && minutes <= 30)
    })

    const eveningEvents = dayEvents.filter((event) => {
      const startTime = new Date(event.event_start)
      const hours = startTime.getHours()
      if (event.end_date === '2025-04-29') {
        return hours >= 16
      }
      return hours >= 18
    })

    const morningTimeSlots = createTimeSlots(morningEvents)
    const eveningTimeSlots = createTimeSlots(eveningEvents)

    return (
      <div className={styles.splitDayView}>
        {morningTimeSlots.length > 0 && (
          <EventGrid timeSlots={morningTimeSlots} venues={venues} dayEvents={morningEvents} />
        )}
        {eveningTimeSlots.length > 0 && (
          <EventGrid timeSlots={eveningTimeSlots} venues={venues} dayEvents={eveningEvents} />
        )}
      </div>
    )
  }

  const renderMobileView = (dayEvents: Event[]) => {
    // Get all events and sort them by time
    const allEvents = venues.reduce<Event[]>((acc, venue) => {
      const venueEvents = groupEventsByVenue(dayEvents)[venue] || []
      return [...acc, ...venueEvents]
    }, [])

    const sortedEvents = allEvents.sort((a, b) => {
      const timeA = +new Date(a.event_start)
      const timeB = +new Date(b.event_start)
      return timeA - timeB
    })

    return (
      <div className={styles.mobileTimeline}>
        {sortedEvents.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    )
  }

  const renderCompactView = (dayEvents: Event[]) => {
    // Group and sort events by venue
    const eventsByVenue = venues.reduce<{ [venue: string]: Event[] }>((acc, venue) => {
      const venueEvents = groupEventsByVenue(dayEvents)[venue] || []
      if (venueEvents.length > 0) {
        acc[venue] = venueEvents.sort((a, b) => {
          const timeA = +new Date(a.event_start)
          const timeB = +new Date(b.event_start)
          return timeA - timeB
        })
      }
      return acc
    }, {})

    return (
      <div className={styles.compactView}>
        {Object.entries(eventsByVenue)
          .sort(([, eventsA], [, eventsB]) => {
            return eventsB.length - eventsA.length
          })
          .map(([venue, events]) => (
            <CompactEventList key={venue} title={venue} events={events} />
          ))}
      </div>
    )
  }

  const renderLiveStreamView = () => {
    // Filter events with video_stream and group by day
    const liveStreamEvents = Object.entries(eventsByDay).reduce<{ [day: string]: Event[] }>((acc, [day, events]) => {
      const streamEvents = events.filter((event) => event.video_stream)
      if (streamEvents.length > 0) {
        acc[day] = streamEvents.sort((a, b) => {
          const timeA = +new Date(a.event_start)
          const timeB = +new Date(b.event_start)
          return timeA - timeB
        })
      }
      return acc
    }, {})

    return (
      <div className={cx(styles.compactView, styles.liveStreamView)}>
        {Object.entries(liveStreamEvents).map(([day, events]) => (
          <CompactEventList key={day} title={format(parseISO(day), 'EEEE, MMMM d')} events={events} />
        ))}
      </div>
    )
  }

  const toggleView = () => {
    const newView = view === 'grid' ? 'compact' : 'grid'
    updateViewInURL(newView)
  }

  // Set initial active tab
  useEffect(() => {
    if (sortedDays.length > 0 && !activeTab) {
      const urlDate = searchParams.get('date')
      if (urlDate && (sortedDays.includes(urlDate) || urlDate === 'LIVE_STREAM')) {
        setActiveTab(urlDate)
      } else {
        const now = getNow()
        const today = format(now, 'yyyy-MM-dd')
        const matchingDay = sortedDays.find((day) => day.startsWith(today))
        const startOfFanfest = '2025-05-01'
        if (matchingDay && +now > +new Date(startOfFanfest)) {
          setActiveTab(matchingDay)
          // Prefer Start of Fanfest date instead of first day in schedule
        } else if (sortedDays.find((day) => day.startsWith(startOfFanfest))) {
          setActiveTab(startOfFanfest)
        } else {
          setActiveTab(sortedDays[0])
        }
      }
    }
  }, [sortedDays, activeTab, searchParams])

  const party = useMemo(() => findPartyEvent(eventsByDay[activeTab] || []), [activeTab, eventsByDay])

  const partyActive =
    party &&
    getEventState({
      ...party,
      event_start: new Date(+new Date(party.event_start) - 1000 * 60 * 60 * 3).toISOString(),
    }) === 'liveEvent'

  return (
    <Container
      id="schedule"
      size="xl"
      className={styles.container}
      style={
        {
          '--time-slot-height': `${TIME_SLOT_HEIGHT}px`,
          '--time-slot-gap': `${TIME_SLOT_GAP}px`,
          position: 'relative',
          zIndex: 1,
        } as React.CSSProperties
      }
    >
      <FadeInSlideUp>
        <Heading
          uppercase
          as="h2"
          variant="semi-bold-36"
          responsive={{ maxSize: 36, minSize: 22 }}
          className={styles.title}
        >
          {title}
        </Heading>
      </FadeInSlideUp>

      <FadeInSlideUp delay={0.2} className={styles.description}>
        {richTextRenderer(description)}
      </FadeInSlideUp>
      <FadeInSlideUp delay={0.4}>
        <div className={styles.scheduleHeader}>
          <div className={styles.headerControls}>
            <div className={styles.tabs}>
              <div className={styles.tabsList}>
                {sortedDays.map((day) => (
                  <button
                    key={day}
                    className={styles.tab}
                    data-active={day === activeTab}
                    onClick={() => updateActiveTab(day)}
                  >
                    {format(parseISO(day), 'EEEE').toUpperCase()}
                  </button>
                ))}
                {/* <button
                  key={'LIVE_STREAM'}
                  className={styles.tab}
                  data-active={'LIVE_STREAM' === activeTab}
                  onClick={() => updateActiveTab('LIVE_STREAM')}
                >
                  LIVE STREAM
                </button> */}
                {/* <div className={styles.liveStreamTabContainer}>
                  <button
                    key={'LIVE_STREAM'}
                    className={cx(styles.tab, styles.liveStreamTab)}
                    data-active={'LIVE_STREAM' === activeTab}
                    onClick={() => updateActiveTab('LIVE_STREAM')}
                  >
                    LIVE STREAM
                  </button>
                </div> */}
                <div className={styles.tabsRightControls}>
                  {
                    <button
                      className={styles.viewToggleButton}
                      onClick={toggleView}
                      title={view === 'grid' ? 'Switch to compact view' : 'Switch to grid view'}
                    >
                      {view === 'grid' ? <IconLayoutListFilled size={20} /> : <IconLayoutGridFilled size={20} />}
                    </button>
                  }
                  <a href="https://evefanfest2026.sched.com/list/simple" className={styles.fullScheduleButton}>
                    See full Schedule{' '}
                    <div className={styles.arrowRight}>
                      <IconArrowRight size={16} />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.tabContent}>
            {sortedDays.map((day) => (
              <div key={day} className={styles.dayContent} hidden={day !== activeTab}>
                {view === 'grid' ? (
                  <>
                    <div className={styles.desktopView}>{renderDesktopView(eventsByDay[day])}</div>
                    <div className={styles.mobileView}>{renderMobileView(eventsByDay[day])}</div>
                  </>
                ) : (
                  renderCompactView(eventsByDay[day])
                )}
              </div>
            ))}
            <div key={'LIVE_STREAM'} className={styles.dayContent} hidden={'LIVE_STREAM' !== activeTab}>
              {renderLiveStreamView()}
            </div>
            {party && (
              <div key={party.id} data-view={view} className={styles.partyAtTheTopOfTheWorld}>
                <div className={styles.partyAtTheTopOfTheWorldContent} ref={ref}>
                  <div className={styles.partyAtTheTopOfTheWorldHeader}>
                    <div className={styles.eventTime}>
                      {formatTime(party.event_start)} - {formatTime(party.event_end)}
                    </div>
                    <div className={styles.partyAtTheTopOfTheWorldTitle}>{party.name}</div>
                  </div>
                </div>
                {visible && (
                  <>
                    <div className={styles.partyAtTheTopOfTheWorldConfetti}>
                      <Confetti delay={500} loop={partyActive} />
                    </div>
                    <div
                      className={styles.partyAtTheTopOfTheWorldConfetti}
                      style={{ transform: 'translateX(calc(min(25vw, 350px))' }}
                    >
                      <Confetti delay={2750} loop={partyActive} />
                    </div>
                    <div
                      className={styles.partyAtTheTopOfTheWorldConfetti}
                      style={{ transform: 'translateX(calc(min(25vw, 350px) * -1))' }}
                    >
                      <Confetti delay={1500} loop={partyActive} />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </FadeInSlideUp>
    </Container>
  )
}

export { Schedule }
